import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useFormTouched } from '@/hooks/use-form-touched';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

function documentTemplates({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/list', {
        params: {
            query: {
                TemplateKind: 'Electrical',
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    Enabled: true,
                    IsDefault: true,
                    ContentKind: true,
                    TemplateKind: true,
                    GroupName: true,
                    IntervalKind: true,
                    Name: true,
                    Description: true,
                },
                Sort: ['Order'],
            },
        },
    });
}

function templatesCreateGet({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/create', {
        params: {
            query: {
                OrganizationId: organisationId,
                TemplateKind: 'Electrical',
            },
        },
    });
}

export const Route = createFileRoute(
    '/(auth)/admin/(organisations)/organisation/templates',
)({
    component: RouteComponent,
    validateSearch: z.object({
        organisationId: z.string().min(1),
    }),
    loaderDeps: ({ search }) => ({
        organisationId: search.organisationId,
    }),
    loader: ({ context, deps }) => {
        context.queryClient.ensureQueryData(documentTemplates(deps));
        context.queryClient.ensureQueryData(templatesCreateGet(deps));
    },

});

function RouteComponent() {
    return (
        <div className="grid grid-cols-5 gap-4">
            <Container className="w-full">
                <Container.Content>
                    <TemplateList />
                </Container.Content>
            </Container>
            <div className="w-full col-span-4 flex flex-col gap-4">
                <Outlet />
            </div>
        </div>
    );
}

function TemplateList() {
    const search = Route.useSearch();

    const { data } = useQuery(documentTemplates(search));

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center">
                <Container.Title>PDF templates</Container.Title>
            </div>
            {data?.Templates?.map(item => (
                <Button key={item.DocumentTemplateId} variant="ghost" className="w-full" asChild>
                    <Link to="/admin/organisation/templates/$templateId" search={search} params={{ templateId: item.DocumentTemplateId! }} activeProps={{ className: 'text-primary' }}>
                        {item.Name}
                    </Link>
                </Button>
            ))}
            <CreateTemplateModal />
        </div>
    );
}

type TemplateFormValues = {
    pdfKind: components['schemas']['DocumentPdfKind'] | null;
    intervalKind: components['schemas']['DocumentTemplateIntervalKind'] | null;
    name: string;
    description: string;
    groupName: string;
    order: number;

};

function CreateTemplateModal() {
    const { t } = useTranslation('admin');
    const [isOpen, setIsOpen] = useState(false);
    const navigate = Route.useNavigate();

    const { organisationId } = Route.useSearch();

    const createTemplateMutation = api.useMutation(
        'post',
        '/api/v2/documents/templates/create',
        {
            onSuccess: ({ DocumentTemplateId }) => {
                queryClient.invalidateQueries(
                    documentTemplates({ organisationId }),
                );
                setIsOpen(false);
                // eslint-disable-next-line ts/no-use-before-define
                reset();
                navigate({
                    to: '/admin/organisation/templates/$templateId',
                    search: { organisationId },
                    params: { templateId: DocumentTemplateId },
                });
            },
        },
    );

    const pdfKinds = useQuery(templatesCreateGet({ organisationId }));

    const defaultValues: TemplateFormValues = {
        order: 999,
        pdfKind: null,
        intervalKind: null,
        name: '',
        description: '',
        groupName: '',
    };

    const { Field, Subscribe, handleSubmit, reset, store } = useForm<TemplateFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            if (!organisationId) {
                throw new Error('Organisation ID is required');
            }

            if (!value.pdfKind) {
                throw new Error('PDF Kind is required');
            }

            if (!value.intervalKind) {
                throw new Error('Interval Kind is required');
            }

            await createTemplateMutation.mutateAsync({
                body: {
                    OrganizationId: organisationId,
                    Description: value.description,
                    Name: value.name,
                    GroupName: value.groupName,
                    Order: value.order,
                    PdfKind: value.pdfKind,
                    IntervalKind: value.intervalKind,
                },
            });
        },
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(t('organisation.templates.confirmClose'))) {
                setIsOpen(false);
                reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                <Button className="w-full">
                    {t('organisation.templates.addButton')}
                </Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('organisation.templates.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <div className="flex flex-col gap-4 py-6">
                        <Field
                            name="name"
                            validators={{
                                onChange:
                                    validators.createDocumentTemplateCommand.name,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.templates.form.name.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.templates.form.name.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="description"
                            validators={{
                                onChange:
                                    validators.createDocumentTemplateCommand.description,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.templates.form.description.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.templates.form.description.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="groupName"
                            validators={{
                                onChange:
                                    validators.createDocumentTemplateCommand.groupName,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('organisation.templates.form.group.label')}>
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('organisation.templates.form.group.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="pdfKind"
                            validators={{
                                onChange:
                                    validators.createDocumentTemplateCommand.pdfKind,
                            }}
                            listeners={{
                                onChange: ({ value, fieldApi }) => {
                                    !pdfKinds?.data?.PdfKinds?.find(
                                        x => x.PdfKind === value,
                                    )?.HasIntervalKind
                                    && fieldApi.form.setFieldValue(
                                        'intervalKind',
                                        'None',
                                    );
                                },
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="PDF Type">
                                    <SingleOptionField
                                        name={name}
                                        handleChange={handleChange}
                                        state={state}
                                        options={[
                                            {
                                                label: 'Electrical Project Price Quote',
                                                value: 'ElectricalProjectPriceQuote',
                                            },
                                            {
                                                label: 'Electrical Project Inspection Plan',
                                                value: 'ElectricalProjectInspectionPlan',
                                            },
                                            {
                                                label: 'Electrical Project Scope 8 Report',
                                                value: 'ElectricalProjectScope8Report',
                                            },
                                            {
                                                label: 'Electrical Project Scope 10 Report',
                                                value: 'ElectricalProjectScope10Report',
                                            },
                                            {
                                                label: 'Electrical Project Scope 12 Report',
                                                value: 'ElectricalProjectScope12Report',
                                            },
                                            {
                                                label: 'Electrical Project Nen 1010 Report',
                                                value: 'ElectricalProjectNen1010Report',
                                            },
                                            {
                                                label: 'Electrical Project Nen 3140 Report',
                                                value: 'ElectricalProjectNen3140Report',
                                            },
                                            {
                                                label: 'Electrical Project Power Shutdown Plan',
                                                value: 'ElectricalProjectPowerShutdownPlan',
                                            },
                                            {
                                                label: 'Electrical Project Serious Defect Report',
                                                value: 'ElectricalProjectSeriousDefectReport',
                                            },
                                        ]}
                                        render={SingleOptionItem}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Subscribe
                            selector={state => [state.values.pdfKind]}
                        >
                            {([type]) =>
                                pdfKinds?.data?.PdfKinds?.find(
                                    x => x.PdfKind === type,
                                )?.HasIntervalKind && (
                                    <Field
                                        name="intervalKind"
                                        validators={{
                                            onChange:
                                                validators.createDocumentTemplateCommand.intervalKind,
                                        }}
                                    >
                                        {({
                                            handleChange,
                                            state,
                                            name,
                                        }) => (
                                            <Label label="Interval Type">
                                                <SingleOptionField
                                                    name={name}
                                                    handleChange={
                                                        handleChange
                                                    }
                                                    state={state}
                                                    options={[
                                                        {
                                                            label: 'First',
                                                            value: 'First',
                                                        },
                                                        {
                                                            label: 'Repeat',
                                                            value: 'Repeat',
                                                        },
                                                    ]}
                                                    render={
                                                        SingleOptionItem
                                                    }
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                )}
                        </Subscribe>
                    </div>
                </PopoutBody>
                <PopoutFooter className="flex flex-col gap-2">
                    <div className="flex w-full gap-2 justify-between items-center">
                        <Button
                            className="w-full"
                            onClick={handleClose}
                            variant="outline"
                            disabled={createTemplateMutation.isPaused}
                        >
                            Annuleren
                        </Button>
                        <Button
                            className="w-full"
                            onClick={
                                handleSubmit
                            }
                            disabled={createTemplateMutation.isPaused}
                        >
                            Opslaan
                        </Button>
                    </div>
                    <div className="flex items-center gap-2">
                        {createTemplateMutation.error?.Message && (
                            <span className="text-red-500">
                                {createTemplateMutation.error.Message}
                            </span>
                        )}
                    </div>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
